import { useState, useMemo } from "react";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import {
  Edit,
  Add,
  WarningAmber,
  SettingsEthernetRounded,
  PublicRounded,
  Telegram
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  Stack,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";
import Error from "../error";
import useUserPermissions from "../../hooks/useUserPermissions";
import {
  mrtManagementTableCustomActionsOuterSx,
  mrtManagementTablePrimaryActionButtonSx,
  mrtManagementTableToolbarOptions,
} from "../../utils/mrtManagementTableToolbar";

// Tenant Auth Methods Management Component
function AuthMethods() {
  const {
    getTenantAuthMethods: canListAuthMethods,
    createSocialLogin: canCreateSocialLogin,
    createTelegramConfig: canCreateTelegramConfig,
    updateSocialLogin: canUpdateSocialLogin,
  } = useUserPermissions();

  // MRT State Management
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([{ id: "name", desc: false }]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // Dialog & Form State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);

  // Form fields
  const [selectedAuthMethodKey, setSelectedAuthMethodKey] = useState("");
  const [botToken, setBotToken] = useState("");
  const [botUserName, setBotUserName] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");

  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();

  // Data Fetching Logic (Server-Side)
  const {
    isLoading: methodsLoading,
    data: methodsData,
    refetch,
    error: fetchError,
  } = useQuery(
    ["tenant-auth-methods", pagination, globalFilter, columnFilters, sorting],
    () => {
      const filterParams = globalFilter
        ? JSON.stringify([{
          column_field: "*",
          operator_value: "contains",
          value: JSON.stringify({
            columns: ["name", "key", "status", "client_id", "bot_user_name"],
            value: globalFilter
          })
        }])
        : columnFilters.length > 0
          ? JSON.stringify(columnFilters.map(f => ({
            column_field: f.id,
            operator_value: "contains",
            value: f.value
          })))
          : "[]";

      const sortParams = JSON.stringify(sorting.map(s => ({
        field: s.id,
        sort: s.desc ? "desc" : "asc"
      })));

      return axiosPrivate.get(`/tenants/auth-methods`, {
        params: {
          page: pagination.pageIndex,
          per_page: pagination.pageSize,
          sort: sortParams,
          filter: filterParams,
        },
      });
    },
    { refetchOnWindowFocus: false, enabled: !!canListAuthMethods }
  );

  // Mutations
  const { mutate: createSocialLogin, isLoading: isCreateSocialLoading } = useMutation(
    (payload) => axiosPrivate.post(`/tenants/social-login`, payload),
    { onSuccess: () => onSaveSuccess("Social login configuration created") }
  );

  const { mutate: updateSocialLogin, isLoading: isUpdateSocialLoading } = useMutation(
    (payload) => axiosPrivate.patch(`/tenants/social-login`, payload),
    { onSuccess: () => onSaveSuccess("Social login configuration updated") }
  );

  const { mutate: createTelegramConfig, isLoading: isCreateTelegramLoading } = useMutation(
    (payload) => axiosPrivate.post(`/tenants/telegram-config`, payload),
    { onSuccess: () => onSaveSuccess("Telegram configuration created") }
  );

  const { mutate: updateTelegramConfig, isLoading: isUpdateTelegramLoading } = useMutation(
    (payload) => axiosPrivate.patch(`/tenants/telegram-config`, payload),
    { onSuccess: () => onSaveSuccess("Telegram configuration updated") }
  );

  const onSaveSuccess = (message) => {
    enqueueSnackbar(message, { variant: "success" });
    handleCloseDialog();
    setSaveConfirmOpen(false);
    refetch();
  };

  // Handlers
  const handleCreate = () => {
    setDialogMode("create");
    setSelectedMethod(null);
    resetForm();
    setDialogOpen(true);
  };

  const handleEdit = (row) => {
    setDialogMode("edit");
    setSelectedMethod(row);
    setSelectedAuthMethodKey(row.key);
    if (row.key === "telegram") {
      setBotToken(row.bot_token || "");
      setBotUserName(row.bot_user_name || "");
      setClientId("");
      setClientSecret("");
    } else {
      setClientId(row.client_id || "");
      setClientSecret(row.client_secret || "");
      setBotToken("");
      setBotUserName("");
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setSelectedAuthMethodKey("");
    setBotToken("");
    setBotUserName("");
    setClientId("");
    setClientSecret("");
  };

  const handleSaveRequest = () => {
    setSaveConfirmOpen(true);
  };

  const handleProceedSave = () => {
    const authKey = dialogMode === "edit" ? selectedMethod?.key : selectedAuthMethodKey;
    if (dialogMode === "edit") {
      if (!canUpdateSocialLogin) {
        enqueueSnackbar("You do not have permission to update social login.", { variant: "error" });
        return;
      }
    } else {
      if (authKey === "telegram" && !canCreateTelegramConfig) {
        enqueueSnackbar("You do not have permission to create Telegram configuration.", { variant: "error" });
        return;
      }
      if (authKey !== "telegram" && !canCreateSocialLogin) {
        enqueueSnackbar("You do not have permission to create social login.", { variant: "error" });
        return;
      }
    }
    if (authKey === "telegram") {
      const payload = { bot_token: botToken, bot_user_name: botUserName };
      dialogMode === "edit" ? updateTelegramConfig(payload) : createTelegramConfig(payload);
    } else {
      const payload = { auth_method_key: authKey, client_id: clientId, client_secret: clientSecret };
      dialogMode === "edit" ? updateSocialLogin(payload) : createSocialLogin(payload);
    }
  };

  // Columns Definition
  const columns = useMemo(() => [
    {
      accessorKey: "name",
      header: "Authentication Provider",
      size: 250,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.lighter', color: 'primary.main', borderRadius: 2 }}>
            {row.original.key === 'telegram' ? <Telegram fontSize="small" /> : <PublicRounded fontSize="small" />}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 800, color: 'secondary.main' }}>
              {row.original.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              Key: {row.original.key}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 150,
      Cell: ({ cell }) => (
        <Chip
          label={cell.getValue()}
          size="small"
          sx={{
            fontWeight: 800,
            fontSize: '11px',
            bgcolor: cell.getValue() === "ENABLED" ? 'rgba(76, 175, 80, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            color: cell.getValue() === "ENABLED" ? '#2e7d32' : 'text.secondary',
          }}
        />
      ),
    },
    {
      accessorKey: "client_id",
      header: "Connection Metadata",
      size: 250,
      Cell: ({ row }) => {
        const isTg = row.original.key === 'telegram';
        const value = isTg ? row.original.bot_user_name : row.original.client_id;
        return (
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
              {isTg ? 'BOT USERNAME' : 'CLIENT ID'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'secondary.main', fontFamily: 'monospace' }}>
              {value || '-'}
            </Typography>
          </Box>
        );
      }
    },
  ], []);

  const table = useMaterialReactTable({
    columns,
    data: methodsData?.data?.data || [],
    initialState: { showColumnFilters: false, density: 'comfortable' },
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    ...mrtManagementTableToolbarOptions,
    muiToolbarAlertBannerProps: fetchError ? { color: 'error', children: 'Error loading data' } : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount: methodsData?.data?.meta_data?.total || 0,
    state: { columnFilters, globalFilter, isLoading: methodsLoading, pagination, sorting },
    enableRowActions: !!canUpdateSocialLogin,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => {
      const key = row.original.key;
      if (key === 'phone' || key === 'email') return null;
      return canUpdateSocialLogin ? (
        <Tooltip title="Configure">
          <IconButton size="small" onClick={() => handleEdit(row.original)} color="primary">
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null;
    },
    renderTopToolbarCustomActions: () => (
      <Box sx={mrtManagementTableCustomActionsOuterSx}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'primary.lighter', display: 'flex', flexShrink: 0 }}>
            <SettingsEthernetRounded sx={{ color: 'primary.main' }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'secondary.main', lineHeight: 1.2 }}>
              Authentication Methods
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Configure how your users access the platform
            </Typography>
          </Box>
        </Stack>
        {(canCreateSocialLogin || canCreateTelegramConfig) && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
            sx={mrtManagementTablePrimaryActionButtonSx}
          >
            Add Method
          </Button>
        )}
      </Box>
    ),
    muiTablePaperProps: {
      elevation: 0,
      sx: { borderRadius: 5, border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 10px 40px rgba(0,0,0,0.02)' },
    },
    muiTableHeadCellProps: {
      sx: { bgcolor: 'background.default', fontWeight: 800, color: 'text.secondary', fontSize: '12px', py: 2 },
    },
  });

  const availableMethods = useMemo(() => {
    return (methodsData?.data?.data || []).filter(m => m.key !== 'phone' && m.key !== 'email');
  }, [methodsData]);

  const availableMethodsForCreate = useMemo(() => {
    return availableMethods.filter((m) =>
      m.key === "telegram" ? canCreateTelegramConfig : canCreateSocialLogin
    );
  }, [availableMethods, canCreateTelegramConfig, canCreateSocialLogin]);

  const canSubmitDialog = useMemo(() => {
    if (dialogMode === "create") {
      if (!selectedAuthMethodKey) return false;
      return selectedAuthMethodKey === "telegram" ? canCreateTelegramConfig : canCreateSocialLogin;
    }
    if (!selectedMethod) return false;
    return canUpdateSocialLogin;
  }, [
    dialogMode,
    selectedAuthMethodKey,
    selectedMethod,
    canCreateTelegramConfig,
    canCreateSocialLogin,
    canUpdateSocialLogin,
  ]);

  const isSaving = isCreateSocialLoading || isUpdateSocialLoading || isCreateTelegramLoading || isUpdateTelegramLoading;

  return fetchError ? (
    <Error error={fetchError?.response?.data?.error} />
  ) : (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: 'background.default', minHeight: '100%' }}>
      <MaterialReactTable table={table} />

      {/* Configuration Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} PaperProps={{ sx: { borderRadius: 6, width: '100%', maxWidth: '450px' } }}>
        <DialogTitle sx={{ fontWeight: 800, borderBottom: '1px solid rgba(0,0,0,0.05)', px: 4, py: 3 }}>
          {dialogMode === "create" ? "Add Authentication Method" : `Update ${selectedMethod?.name}`}
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {dialogMode === "create" && (
              <FormControl fullWidth size="small">
                <InputLabel>Select Provider</InputLabel>
                <Select
                  value={selectedAuthMethodKey}
                  label="Select Provider"
                  onChange={(e) => setSelectedAuthMethodKey(e.target.value)}
                  sx={{ borderRadius: 3 }}
                >
                  {availableMethodsForCreate.map((m) => (
                    <MenuItem key={m.key} value={m.key}>{m.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {(selectedAuthMethodKey === 'telegram' || (selectedMethod?.key === 'telegram' && dialogMode === 'edit')) ? (
              <>
                <TextField
                  fullWidth
                  size="small"
                  label="Bot Username"
                  value={botUserName}
                  onChange={(e) => setBotUserName(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Bot Token"
                  type="password"
                  value={botToken}
                  onChange={(e) => setBotToken(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
              </>
            ) : (selectedAuthMethodKey || (selectedMethod && dialogMode === 'edit')) ? (
              <>
                <TextField
                  fullWidth
                  size="small"
                  label="Client ID"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Client Secret"
                  type="password"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 4, gap: 1 }}>
          <Button fullWidth variant="outlined" color="inherit" onClick={handleCloseDialog} sx={{ borderRadius: 3, fontWeight: 700, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSaveRequest}
            disabled={!canSubmitDialog}
            sx={{ borderRadius: 3, fontWeight: 700, textTransform: 'none' }}
          >
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Confirmation */}
      <SafetyDialog
        open={saveConfirmOpen}
        title="Confirm Configuration?"
        content="Applying these changes will immediately impact how users authenticate using this provider. Ensure all credentials are correct."
        onConfirm={handleProceedSave}
        onCancel={() => setSaveConfirmOpen(false)}
        isLoading={isSaving}
        color="primary"
      />
    </Box>
  );
}

function SafetyDialog({ open, title, content, onConfirm, onCancel, isLoading, color = "primary" }) {
  return (
    <Dialog open={open} onClose={() => !isLoading && onCancel()} PaperProps={{ sx: { borderRadius: 6, p: 1, maxWidth: '400px' } }}>
      <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
        <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: `${color}.lighter`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
          <WarningAmber color={color} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>{content}</Typography>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
        <Button fullWidth variant="outlined" color="inherit" onClick={onCancel} disabled={isLoading} sx={{ borderRadius: 3, fontWeight: 600, textTransform: 'none' }}>
          Cancel
        </Button>
        <Button fullWidth variant="contained" color={color} onClick={onConfirm} disabled={isLoading} sx={{ borderRadius: 3, fontWeight: 700, textTransform: 'none' }}>
          {isLoading ? <CircularProgress size={24} color="inherit" /> : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AuthMethods;
